import { useEffect, useMemo, useRef, useState } from 'react'

import { Icon } from '@/icons/Icon'
import { cn, scrollToSection } from '@/lib/utils'
import { usePageContext } from './PageContext'
import { useBreakpoint } from '@/hooks/useBreakpoint'

type FilterSearchProps = {
  placeholder?: string
  overlay?: boolean
}

export default function FilterSearch({ placeholder, overlay }: FilterSearchProps) {
  const { query, setQuery } = usePageContext()
  const { xs } = useBreakpoint()

  const [width, setWidth] = useState(128)
  const [focused, setFocused] = useState(false)
  const [highlighted, setHighlighted] = useState(false)

  const inputRef = useRef<HTMLInputElement>(null)
  const measureRef = useRef<HTMLSpanElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const scrollToValues = useMemo(() => (overlay ? { mobile: 64, desktop: 56 } : 80), [overlay])

  useEffect(() => {
    if (measureRef.current) {
      const textWidth = measureRef.current.offsetWidth
      setWidth(textWidth + 115)
    }
  }, [placeholder])

  useEffect(() => {
    if (focused && inputRef.current) {
      inputRef.current.focus()
      setHighlighted(true)
      scrollToSection({ id: 'filters', offset: scrollToValues, animate: true, hideHash: true })
    } else {
      inputRef.current?.blur()
      setHighlighted(false)
    }
  }, [focused])

  useEffect(() => {
    const handleKeydown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && focused) {
        handleEscape()
      }

      if (e.key === 'Enter' && focused && query) {
        setHighlighted(false)
        setFocused(false)
      }

      // Check if any input is currently focused
      const activeElement = document.activeElement
      const isInputFocused =
        activeElement instanceof HTMLInputElement ||
        activeElement instanceof HTMLTextAreaElement ||
        (activeElement instanceof HTMLElement && activeElement.isContentEditable)

      if (isInputFocused) return

      if (e.key === 'f' && !e.metaKey && !e.ctrlKey && !e.altKey && !e.shiftKey) {
        e.preventDefault()
        setFocused(true)
      }
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setHighlighted(false)
        setFocused(false)
      }
    }

    window.addEventListener('keydown', handleKeydown)
    if (focused) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => {
      if (focused) {
        document.removeEventListener('mousedown', handleClickOutside)
      }
      window.removeEventListener('keydown', handleKeydown)
    }
  }, [focused, query, highlighted])

  const clearSearch = () => {
    setQuery('')
    scrollToSection({ id: 'filters', offset: scrollToValues, animate: true, hideHash: true })

    if (focused && inputRef.current) {
      inputRef.current.focus()
      setHighlighted(true)
    }
  }

  const focusSearch = () => {
    setFocused(true)
    setHighlighted(true)
  }

  const handleEscape = (exit?: boolean) => {
    if (exit) {
      clearSearch()
      setFocused(false)
      setHighlighted(false)
      return
    }

    if (query) {
      clearSearch()
    } else {
      setFocused(false)
      setHighlighted(false)
    }
  }

  return (
    <>
      <span ref={measureRef} className="invisible absolute text-16 md:text-14" aria-hidden="true">
        {placeholder}
      </span>
      <div
        ref={containerRef}
        className={cn(
          'relative flex h-9 items-center gap-2.5 overflow-hidden rounded-full border px-2.5 outline outline-primary-100 transition-all duration-300 dark:outline-primary-500/40',
          highlighted
            ? 'border-primary-500 outline-2'
            : 'border-gray-200 outline-0 dark:border-white/10',
        )}
        style={{ width: !xs ? '100%' : focused ? '250px' : `${width}px` }}
        onClick={focusSearch}
      >
        <Icon
          name="magnifier"
          className={cn(
            'cursor-pointer transition-colors duration-300',
            highlighted ? 'text-primary-500' : 'text-gray-400 dark:text-white/60',
          )}
        />
        <input
          ref={inputRef}
          value={query}
          type="text"
          placeholder={`Filter ${placeholder ? placeholder.charAt(0).toUpperCase() + placeholder.slice(1) : ''}`}
          aria-label={`Filter ${placeholder ? placeholder.charAt(0).toUpperCase() + placeholder.slice(1) : ''}`}
          className="h-full w-full border-none bg-transparent text-16 outline-none md:text-14"
          onChange={(e) => setQuery(e.target.value)}
        />

        <div
          onClick={(e) => {
            e.stopPropagation()
            handleEscape()
          }}
          className={cn(
            'relative z-10 hidden h-4 w-fit items-center justify-center rounded-key border-[0.75px] border-black/10 bg-black/10 px-1 text-[10px] font-medium leading-none text-black/50 dark:border-white/10 dark:bg-white/10 dark:text-white/80 navigation:flex',
            focused ? 'cursor-pointer' : 'cursor-default',
          )}
        >
          {focused ? 'Esc' : 'F'}
        </div>
      </div>
    </>
  )
}
